import React, { useState, useEffect, useContext } from "react";
import { Container, Card, CardBody } from "reactstrap";
import DataTable from "react-data-table-component";
import { getOrdersByProccesingPlantId } from "../../../../api/order/orderApi";
import { updateProccesingPlantStatus } from "../../../../api/stock/proccesingPlantStatusChange";
import { getPlantByUserId } from '../../../../api/processing plant/plantApi';
import { getTruckByPlantId } from '../../../../api/TruckDriver/TruckDriver';
import { createOrder } from '../../../../api/order/orderApi';
import Swal from 'sweetalert2';
import { motion } from 'framer-motion';
import { AuthContext } from '../../../ExtraPages/AuthProvider';
import { useNavigate } from 'react-router-dom';

const ProcessingPlantStockAccept = () => {
  // Declare state variables
  const [stockData, setStockData] = useState([]);
  const [plantId, setPlantId] = useState(null);
  const [truckDrivers, setTruckDrivers] = useState([]);
  const { user, loading } = useContext(AuthContext);
  const navigate = useNavigate();

  // Document types for upload
  const documentTypes = [
    'Invoice',
    'Packing List',
    'Airway Bill',
    'Health Certificate',
    'Catch Certificate',
    'Histamine Report',
    'Mercury Report',
  ];

  // Map document types to backend field names
  const documentTypeMapping = {
    Invoice: 'invoice',
    'Packing List': 'package_list',
    'Airway Bill': 'airway_bill',
    'Health Certificate': 'health_certificate',
    'Catch Certificate': 'catch_certificate',
    'Histamine Report': 'histamine_report',
    'Mercury Report': 'mercury_Report',
  };

  // Fetch presigned URL for existing file
  const fetchPresignedUrl = async (key) => {
    const API_BASE_URL = 'https://api.starmarketapi.online/orders1';
    const accessToken = localStorage.getItem('accessToken');

    try {
      const response = await fetch(
        `${API_BASE_URL}/generate-download-url?key=${encodeURIComponent(key)}`,
        {
          method: 'GET',
          headers: {
            ...(accessToken && { Authorization: `Bearer ${accessToken}` }),
          },
        }
      );

      if (!response.ok) {
        throw new Error('Failed to fetch presigned URL');
      }

      const data = await response.json();
      return data.downloadUrl;
    } catch (error) {
      console.error('Error fetching presigned URL:', error);
      return null;
    }
  };

  // Upload file to backend
  const uploadFileToBackend = async (id, filetype, file) => {
    const API_BASE_URL = 'https://api.starmarketapi.online/orders1';
    const accessToken = localStorage.getItem('accessToken');

    const formData = new FormData();
    formData.append('id', id);
    formData.append('filetype', filetype);
    formData.append('file', file);

    console.log('Upload Payload:', {
      id,
      filetype,
      file,
    });

    const response = await fetch(`${API_BASE_URL}/upload-file`, {
      method: 'POST',
      headers: {
        ...(accessToken && { Authorization: `Bearer ${accessToken}` }),
      },
      body: formData,
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error('Backend response:', errorText);
      throw new Error(
        `Failed to upload file: ${response.status} ${response.statusText}`
      );
    }

    const data = await response.json();
    return data;
  };

  // Fetch plantId based on user.uid
  useEffect(() => {
    const fetchPlantId = async () => {
      try {
        if (user?.uid) {
          const response = await getPlantByUserId(user.uid);
          console.log('Plant response:', response.data);
          // Extract the _id from the plant object
          setPlantId(response.data._id);
        }
      } catch (error) {
        console.error('Error fetching plant ID:', error);
        Swal.fire('Error!', 'Failed to fetch plant ID.', 'error');
      }
    };

    fetchPlantId();
  }, [user]);

  // Fetch stock data and truck drivers when plantId is available
  useEffect(() => {
    if (!plantId) return;

    const fetchPlantsStockData = async () => {
      try {
        console.log('Fetching stock data for plantId:', plantId);
        const response = await getOrdersByProccesingPlantId(user.uid);
        console.log('Stock data response:', response.data.data);

        if (Array.isArray(response.data.data)) {
          setStockData(response.data.data);
        } else {
          console.error('Unexpected stock data format:', response.data);
          setStockData([]);
        }
      } catch (error) {
        console.error('Error fetching stock data:', error.message);
        setStockData([]);
      }
    };

    const fetchTruckDrivers = async () => {
      try {
        console.log('Fetching truck drivers for plantId:', plantId);
        const response = await getTruckByPlantId(plantId);
        console.log('Truck drivers response:', response);

        if (Array.isArray(response.data.data)) {
          setTruckDrivers(response.data.data);
        } else {
          console.error('Unexpected truck drivers format:', response.data);
          setTruckDrivers([]);
        }
      } catch (error) {
        console.error('Error fetching truck drivers:', error.message);
        setTruckDrivers([]);
      }
    };

    fetchPlantsStockData();
    fetchTruckDrivers();
  }, [plantId]);

  // Handle accepting stock
  const handleAccept = async (id) => {
    Swal.fire({
      title: 'Set Estimated Processing Time & Assign Truck Driver',
      html: `
        <div style="display: flex; flex-direction: column; gap: 15px;">
          <div style="display: flex; justify-content: center; align-items: center; gap: 10px;">
            <input type="number" id="days" class="swal2-input" placeholder="Days" min="1" 
              style="width: 100px; height: 35px; font-size: 14px; text-align: center;">
            <input type="number" id="hours" class="swal2-input" placeholder="Hours" min="0" max="23" 
              style="width: 100px; height: 35px; font-size: 14px; text-align: center;">
          </div>
          <select id="truckDriver" class="swal2-select" style="width: 80%; height: 35px; font-size: 14px;">
            <option value="" disabled selected>Select Truck Driver</option>
            ${truckDrivers
              .map(
                (driver) =>
                  `<option value="${driver._id}">${
                    driver.firstName || 'Unnamed'
                  } ${driver.lastName || 'Driver'}</option>`
              )
              .join('')}
          </select>
        </div>
      `,
      showCancelButton: true,
      confirmButtonText: 'Set & Accept',
      preConfirm: () => {
        const days = parseInt(document.getElementById('days').value);
        const hours = parseInt(document.getElementById('hours').value) || 0;
        const truckDriverId = document.getElementById('truckDriver').value;

        if (!truckDriverId) {
          Swal.showValidationMessage('Please select a truck driver');
          return false;
        }
        if (!days || days < 1) {
          Swal.showValidationMessage('Days must be at least 1');
          return false;
        }

        return {
          totalHours: days * 24 + hours,
          truckDriverId,
        };
      },
    }).then(async (result) => {
      if (result.isConfirmed) {
        const { totalHours, truckDriverId } = result.value;
        const data = [
          {
            Order_id: id,
            Processing_Plant_status: true,
            EPT: totalHours,
            truckDriverId,
          },
        ];

        console.log('Form data to submit:', data);

        try {
          const response = await createOrder(data);
          console.log('Form submitted successfully:', response.data);
          Swal.fire(
            'Accepted!',
            'The stock has been accepted and truck driver assigned.',
            'success'
          );

          // Refresh stock data
          const responseStock = await getOrdersByProccesingPlantId(user.uid);
          if (Array.isArray(responseStock.data.data)) {
            setStockData(responseStock.data.data);
          } else {
            setStockData([]);
          }
        } catch (error) {
          console.error('Error submitting form:', error);
          Swal.fire('Error!', 'Something went wrong.', 'error');
        }
      }
    });
  };

  // Handle rejecting stock
  const handleReject = async (id) => {
    const data = [
      {
        Order_id: id,
        Processing_Plant_status: false,
        EPT: '',
        truckDriverId: '',
      },
    ];

    Swal.fire({
      title: 'Are you sure?',
      text: "You won't be able to revert this!",
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      confirmButtonText: 'Yes, reject it!',
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          const response = await createOrder(data);
          console.log('Form submitted successfully:', response.data);
          Swal.fire('Rejected!', 'The stock has been rejected.', 'success');

          // Refresh stock data
          const responseStock = await getOrdersByProccesingPlantId(user.uid);
          if (Array.isArray(responseStock.data.data)) {
            setStockData(responseStock.data.data);
          } else {
            setStockData([]);
          }
        } catch (error) {
          console.error('Error submitting form:', error);
          Swal.fire('Error!', 'Something went wrong.', 'error');
        }
      }
    });
  };

  // Handle document upload with previews - UPDATED VERSION
  const handleUpload = async (orderId) => {
    // Find the order data to check for existing files
    const orderData = stockData.find((item) => item._id === orderId);

    // Function to create file preview for existing uploaded files
    const createExistingFilePreview = async (
      fileKey,
      previewId,
      documentType
    ) => {
      const previewDiv = document.getElementById(previewId);
      if (!previewDiv) return;

      previewDiv.innerHTML =
        '<div style="font-size: 12px; color: #007bff;">Loading existing file...</div>';

      try {
        const presignedUrl = await fetchPresignedUrl(fileKey);

        if (presignedUrl) {
          const fileExtension = fileKey.split('.').pop().toLowerCase();
          const fileName = fileKey.split('/').pop();

          let previewHTML = `
            <div style="border: 2px solid #28a745; padding: 10px; border-radius: 8px; background-color: #f8f9fa; margin-top: 8px;">
              <div style="font-size: 12px; color: #28a745; font-weight: bold; margin-bottom: 5px;">✓ Existing File</div>
              <div style="font-size: 11px; color: #666; margin-bottom: 8px;">${fileName}</div>
          `;

          if (['jpg', 'jpeg', 'png'].includes(fileExtension)) {
            previewHTML += `<img src="${presignedUrl}" style="max-width: 200px; max-height: 150px; border: 1px solid #ddd; border-radius: 4px; display: block; margin: 0 auto;" />`;
          } else if (fileExtension === 'pdf') {
            previewHTML += `
              <div style="text-align: center;">
                <i style="font-size: 32px; color: #dc3545;">📄</i>
                <div style="font-size: 11px; color: #007bff;">PDF Document</div>
              </div>
            `;
          } else {
            previewHTML += `
              <div style="text-align: center;">
                <i style="font-size: 32px; color: #007bff;">📎</i>
                <div style="font-size: 11px; color: #007bff;">Document File</div>
              </div>
            `;
          }

          previewHTML += `
              <a href="${presignedUrl}" target="_blank" style="display: inline-block; margin-top: 8px; padding: 4px 12px; background-color: #007bff; color: white; text-decoration: none; border-radius: 4px; font-size: 11px;">View/Download</a>
              <div style="font-size: 10px; color: #6c757d; margin-top: 5px;">Upload a new file to replace</div>
            </div>
          `;

          previewDiv.innerHTML = previewHTML;
        } else {
          previewDiv.innerHTML =
            '<div style="font-size: 11px; color: #dc3545;">Failed to load existing file</div>';
        }
      } catch (error) {
        console.error('Error creating existing file preview:', error);
        previewDiv.innerHTML =
          '<div style="font-size: 11px; color: #dc3545;">Error loading file</div>';
      }
    };

    // Function to create file preview for newly selected files
    const createFilePreview = (file, previewId) => {
      const previewDiv = document.getElementById(previewId);
      previewDiv.innerHTML = ''; // Clear previous preview

      if (file) {
        const fileType = file.type;
        const validImageTypes = ['image/jpeg', 'image/png', 'image/jpg'];
        const validPdfType = 'application/pdf';
        const validDocTypes = [
          'application/msword',
          'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        ];

        // Create file info
        const fileInfo = document.createElement('div');
        fileInfo.style.cssText =
          'font-size: 12px; color: #666; margin-bottom: 5px;';
        fileInfo.innerHTML = `<strong>${file.name}</strong> (${(
          file.size / 1024
        ).toFixed(1)} KB)`;
        previewDiv.appendChild(fileInfo);

        if (validImageTypes.includes(fileType)) {
          const img = document.createElement('img');
          img.src = URL.createObjectURL(file);
          img.style.cssText =
            'max-width: 200px; max-height: 150px; border: 1px solid #ddd; border-radius: 4px; margin-top: 8px; display: block;';
          previewDiv.appendChild(img);
        } else if (fileType === validPdfType) {
          const pdfContainer = document.createElement('div');
          pdfContainer.style.cssText = 'text-align: center; margin-top: 8px;';
          pdfContainer.innerHTML = `
            <div style="display: inline-block; padding: 10px; border: 2px dashed #007bff; border-radius: 8px; background-color: #f8f9fa;">
              <i style="font-size: 32px; color: #dc3545; margin-bottom: 5px;">📄</i>
              <div style="font-size: 12px; color: #007bff; font-weight: bold;">PDF File</div>
              <div style="font-size: 10px; color: #6c757d;">Click to view after upload</div>
            </div>
          `;
          previewDiv.appendChild(pdfContainer);
        } else if (validDocTypes.includes(fileType)) {
          const docContainer = document.createElement('div');
          docContainer.style.cssText = 'text-align: center; margin-top: 8px;';
          docContainer.innerHTML = `
            <div style="display: inline-block; padding: 10px; border: 2px dashed #28a745; border-radius: 8px; background-color: #f8f9fa;">
              <i style="font-size: 32px; color: #007bff; margin-bottom: 5px;">📝</i>
              <div style="font-size: 12px; color: #007bff; font-weight: bold;">Word Document</div>
              <div style="font-size: 10px; color: #6c757d;">Document ready for upload</div>
            </div>
          `;
          previewDiv.appendChild(docContainer);
        } else {
          const unknownContainer = document.createElement('div');
          unknownContainer.style.cssText =
            'text-align: center; margin-top: 8px;';
          unknownContainer.innerHTML = `
            <div style="display: inline-block; padding: 10px; border: 2px dashed #6c757d; border-radius: 8px; background-color: #f8f9fa;">
              <i style="font-size: 32px; color: #6c757d; margin-bottom: 5px;">📎</i>
              <div style="font-size: 12px; color: #6c757d; font-weight: bold;">Unknown File Type</div>
              <div style="font-size: 10px; color: #dc3545;">May not be supported</div>
            </div>
          `;
          previewDiv.appendChild(unknownContainer);
        }
      }
    };

    Swal.fire({
      title: 'Upload Documents',
      html: `
        <div class="d-flex flex-column gap-3" style="max-height: 400px; overflow-y: auto; padding: 10px;">
          ${documentTypes
            .map(
              (type) => `
                <div class="mb-3 w-100">
                  <label for="${type.replace(
                    /\s+/g,
                    '-'
                  )}" class="form-label fw-bold text-secondary small">${type}</label>
                  <input type="file" id="${type.replace(/\s+/g, '-')}" 
                         class="form-control form-control-sm w-75 mx-auto document-file" 
                         accept=".pdf,.jpg,.png,.jpeg,.doc,.docx" 
                         data-document-type="${type}">
                  <div id="preview-${type.replace(
                    /\s+/g,
                    '-'
                  )}" class="mt-2 text-center"></div>
                </div>
              `
            )
            .join('')}
        </div>
      `,
      showCancelButton: true,
      confirmButtonText: 'Upload All',
      customClass: {
        popup: 'swal-wide',
      },
      width: '700px',
      didOpen: async () => {
        // Show existing files if available
        if (orderData) {
          for (const type of documentTypes) {
            const backendFieldName = documentTypeMapping[type];
            const existingFileKey = orderData[backendFieldName];

            if (existingFileKey) {
              const previewId = 'preview-' + type.replace(/\s+/g, '-');
              await createExistingFilePreview(existingFileKey, previewId, type);
            }
          }
        }

        // Add event listeners for file inputs after the modal opens
        document.querySelectorAll('.document-file').forEach((input) => {
          input.addEventListener('change', function () {
            const file = this.files[0];
            const previewId = 'preview-' + this.id;
            createFilePreview(file, previewId);
          });
        });
      },
      preConfirm: () => {
        const filesData = [];
        let hasNewFile = false;
        let hasExistingFile = false;

        // Collect newly selected files
        documentTypes.forEach((type) => {
          const input = document.getElementById(type.replace(/\s+/g, '-'));
          const file = input.files[0];
          if (file) {
            hasNewFile = true;
            filesData.push({ documentType: type, file });
          }

          // Check if there's an existing file
          const backendFieldName = documentTypeMapping[type];
          if (orderData && orderData[backendFieldName]) {
            hasExistingFile = true;
          }
        });

        // If no new files and no existing files, show error
        if (!hasNewFile && !hasExistingFile) {
          Swal.showValidationMessage('Please upload at least one document');
          return false;
        }

        // If only existing files, show success message
        if (!hasNewFile && hasExistingFile) {
          Swal.fire({
            title: 'No New Files',
            text: 'No new files were selected. Existing documents are already uploaded.',
            icon: 'info',
            confirmButtonText: 'OK',
          });
          return false;
        }

        return filesData;
      },
    }).then(async (result) => {
      if (result.isConfirmed) {
        const filesData = result.value;
        let uploadedCount = 0;
        const totalFiles = filesData.length;
        const failedUploads = [];

        try {
          // Show loading indicator with progress
          Swal.fire({
            title: 'Uploading Documents...',
            html: `
              <div style="display: flex; flex-direction: column; align-items: center; gap: 15px;">
                <div class="spinner-border text-primary" role="status">
                  <span class="sr-only">Loading...</span>
                </div>
                <div>Please wait while we upload your documents</div>
                <div id="upload-progress" style="font-size: 14px; color: #007bff; font-weight: bold;">
                  Uploading 0 of ${totalFiles}
                </div>
              </div>
            `,
            allowOutsideClick: false,
            allowEscapeKey: false,
            showConfirmButton: false,
            didOpen: () => {
              Swal.showLoading();
            },
          });

          console.log('Starting document uploads for orderId:', orderId);

          // Upload each file one by one
          for (const { documentType, file } of filesData) {
            try {
              // Map document type to backend field name
              const backendFiletype = documentTypeMapping[documentType];
              if (!backendFiletype) {
                console.error(`Unknown document type: ${documentType}`);
                failedUploads.push({
                  documentType,
                  error: 'Unknown document type',
                });
                continue;
              }

              console.log(`Uploading ${documentType} (${file.name})...`);

              // Upload file to backend
              const result = await uploadFileToBackend(
                orderId,
                backendFiletype,
                file
              );

              console.log(`Successfully uploaded ${documentType}:`, result);

              uploadedCount++;
              console.log(
                `Successfully uploaded ${documentType} (${uploadedCount}/${totalFiles})`
              );

              // Update progress
              const progressDiv = document.getElementById('upload-progress');
              if (progressDiv) {
                progressDiv.innerHTML = `Uploading ${uploadedCount} of ${totalFiles}`;
              }
            } catch (error) {
              console.error(`Error uploading ${documentType}:`, error);
              failedUploads.push({ documentType, error: error.message });
            }
          }

          // Show final result
          if (failedUploads.length === 0) {
            // Refresh order data
            try {
              const responseStock = await getOrdersByProccesingPlantId(
                user.uid
              );
              if (Array.isArray(responseStock.data.data)) {
                setStockData(responseStock.data.data);
              }
            } catch (error) {
              console.error('Error refreshing order data:', error);
            }

            Swal.fire({
              title: 'Success!',
              text: `All ${uploadedCount} document(s) have been uploaded successfully.`,
              icon: 'success',
              confirmButtonText: 'OK',
            });
          } else if (uploadedCount > 0) {
            // Refresh order data even with partial success
            try {
              const responseStock = await getOrdersByProccesingPlantId(
                user.uid
              );
              if (Array.isArray(responseStock.data.data)) {
                setStockData(responseStock.data.data);
              }
            } catch (error) {
              console.error('Error refreshing order data:', error);
            }

            Swal.fire({
              title: 'Partial Success',
              html: `
                <div>
                  <p>${uploadedCount} document(s) uploaded successfully.</p>
                  <p style="color: #dc3545;">${
                    failedUploads.length
                  } document(s) failed:</p>
                  <ul style="text-align: left; font-size: 12px;">
                    ${failedUploads
                      .map((f) => `<li>${f.documentType}: ${f.error}</li>`)
                      .join('')}
                  </ul>
                </div>
              `,
              icon: 'warning',
              confirmButtonText: 'OK',
            });
          } else {
            Swal.fire({
              title: 'Upload Failed!',
              html: `
                <div>
                  <p>Failed to upload documents:</p>
                  <ul style="text-align: left; font-size: 12px;">
                    ${failedUploads
                      .map((f) => `<li>${f.documentType}: ${f.error}</li>`)
                      .join('')}
                  </ul>
                </div>
              `,
              icon: 'error',
              confirmButtonText: 'OK',
            });
          }
        } catch (error) {
          console.error('Error during upload process:', error);
          Swal.fire({
            title: 'Upload Failed!',
            text: 'An unexpected error occurred. Please try again.',
            icon: 'error',
            confirmButtonText: 'OK',
          });
        } finally {
          // Clean up object URLs to prevent memory leaks
          filesData.forEach(({ file }) => {
            if (file) {
              URL.revokeObjectURL(URL.createObjectURL(file));
            }
          });
        }
      }
    });
  };

  // Handle tracking
  const handleTracking = (truckDriverId, Stock_id, Trip_Number) => {
    navigate(`/truckTracking/${truckDriverId}/${Stock_id}/${Trip_Number}`);
  };

  // Define columns for DataTable
  const columns = [
    { name: 'Fish Type', selector: (row) => row.Product_Name, sortable: true },
    { name: 'Quantity', selector: (row) => row.quantity, sortable: true },
    { name: 'Vessel Name', selector: (row) => row.boat_number, sortable: true },
    { name: 'Harbour', selector: (row) => row.Harbour, sortable: true },
    { name: 'Order Date', selector: (row) => row.Order_Date, sortable: true },

    // Status column
    {
      name: 'Status',
      cell: (row) => {
        if (row.Processing_Plant_status === 'false') {
          return <span className="status-button-danger">Rejected</span>;
        } else if (row.Processing_Plant_status === 'true') {
          return <span className="status-button-success">Accepted</span>;
        } else {
          return <span className="status-button-warning">Pending</span>;
        }
      },
      sortable: true,
    },

    // Actions column
    {
      name: 'Actions',
      cell: (row) => (
        <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
          {row.Processing_Plant_status === 'true' ? (
            // Accepted → Tracking + Upload
            <>
              <button
                className="table-button table-button-info"
                size="sm"
                onClick={() =>
                  handleTracking(row.truckDriverId, row.stock_id, row.trip_id)
                }
              >
                Tracking
              </button>
              <button
                className="table-button table-button-primary"
                size="sm"
                onClick={() => handleUpload(row._id)}
              >
                Upload
              </button>
            </>
          ) : row.Processing_Plant_status === 'false' ? null : ( // Rejected → No buttons
            // Pending → Accept + Reject
            <>
              <button
                className="table-button table-button-success"
                size="sm"
                onClick={() => handleAccept(row.Order_id)}
              >
                Accept
              </button>
              <button
                className="table-button table-button-danger"
                size="sm"
                onClick={() => handleReject(row.Order_id)}
              >
                Reject
              </button>
            </>
          )}
        </div>
      ),
    },
  ];

  // Component for the expandable row
  const ExpandableRowComponent = ({ data }) => (
    <div
      style={{
        backgroundColor: '#dfd9d9ff',
        padding: '20px',
        fontFamily: 'Arial, sans-serif',
        color: '#333',
      }}
    >
      <h6 style={{ fontSize: '18px', fontWeight: 'bold' }}>
        Additional Order Details
      </h6>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '40px' }}>
        {/* Left Column */}
        <div style={{ flex: '1', minWidth: '280px' }}>
          <div style={rowStyle}>
            <strong>Buyer Name:</strong>
            <span>
              {data.buyerDetails[0]?.firstName +
                ' ' +
                data.buyerDetails[0]?.lastName || 'N/A'}
            </span>
          </div>
          <div style={rowStyle}>
            <strong>Buyer Email:</strong>
            <span>{data.buyerDetails[0]?.email || 'N/A'}</span>
          </div>
          <div style={rowStyle}>
            <strong>Buyer Phone:</strong>
            <span>{data.buyerDetails[0]?.contactNumber || 'N/A'}</span>
          </div>
          <div style={rowStyle}>
            <strong>Delivery Address:</strong>
            <span>
              {(data.buyerDetails[0]?.address?.line1 || '') +
                ' ' +
                (data.buyerDetails[0]?.address?.line2 || '') || 'N/A'}
            </span>
          </div>
          <div style={rowStyle}>
            <strong>Processing Fee:</strong>
            <span>{data.Processing_Fee || 'N/A'}</span>
          </div>
        </div>
        {/* Right Column */}
        <div style={{ flex: '1', minWidth: '280px' }}>
          <div style={rowStyle}>
            <strong>Processing Types:</strong>
            <div>
              {(data.Processing_Type || 'N/A').split(',').map((type, index) => (
                <span
                  key={index}
                  style={{
                    display: 'inline-block',
                    backgroundColor: 'rgb(0, 59, 143)',
                    color: '#fff',
                    padding: '4px 12px',
                    borderRadius: '15px',
                    marginRight: '6px',
                    marginBottom: '4px',
                    fontSize: '12px',
                  }}
                >
                  {type.trim()}
                </span>
              ))}
            </div>
          </div>
          <div style={rowStyle}>
            <strong>EPT:</strong>
            <span>
              {data.EPT
                ? `${Math.floor(data.EPT / 24)} day(s) ${data.EPT % 24} hour(s)`
                : 'N/A'}
            </span>
          </div>
          <div style={rowStyle}>
            <strong>Quality Grade:</strong>
            <span>{data.Quality_Grade || 'N/A'}</span>
          </div>
          <div style={rowStyle}>
            <strong>Delivery Method:</strong>
            <span>{data.Courier_Method || 'N/A'}</span>
          </div>
          <div style={rowStyle}>
            <strong>Delivery Date:</strong>
            <span>{data.Delivery_Date || 'N/A'}</span>
          </div>
        </div>
      </div>
    </div>
  );

  const rowStyle = {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '10px',
    borderBottom: '1px solid #eee',
    paddingBottom: '6px',
    fontSize: '15px',
  };

  // Handle loading and authentication states
  if (loading) {
    return (
      <Container fluid>
        <Card>
          <CardBody>
            <h5>Loading...</h5>
          </CardBody>
        </Card>
      </Container>
    );
  }

  if (!user) {
    return (
      <Container fluid>
        <Card>
          <CardBody>
            <h5>Please log in to view processing approvals.</h5>
          </CardBody>
        </Card>
      </Container>
    );
  }

  if (!plantId) {
    return (
      <Container fluid>
        <Card>
          <CardBody>
            <h5>Loading plant data...</h5>
          </CardBody>
        </Card>
      </Container>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: -20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      transition={{ duration: 0.5 }}
    >
      <div style={{ minHeight: '100%' }}>
        <Container fluid>
          <div style={{ overflowX: 'auto' }}>
            <Card style={{ backgroundColor: '#fff' }}>
              <CardBody style={{ padding: '20px' }}>
                <h5 className="mb-4">Processing Approval</h5>
                <DataTable
                  columns={columns}
                  data={stockData}
                  pagination
                  expandableRows
                  expandableRowsComponent={ExpandableRowComponent}
                />
              </CardBody>
            </Card>
          </div>
        </Container>
      </div>
    </motion.div>
  );
};

export default ProcessingPlantStockAccept;
